import { Button, Grid } from '@mui/material';
import type { CellContext } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as _ from 'lodash';
import type { BaseSyntheticEvent, SyntheticEvent } from 'react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classSet from 'react-classset';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { MAX_FILE_SIZE_20GB } from '@/constants';
import { useProgress } from '@/contexts/ProgressProvider';
import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import { AssetSource, MessageType, Permission, PushNotificationEventType } from '@/enums';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { VNBaseModal } from '@/modals/VNBaseModal';
import type { Item, ListItem, Pagination } from '@/models';
import { AccessLevelType } from '@/models/channel';
import AssetDetailsModal from '@/pages/assets/asset-details-modal/AssetDetailsModal';
import { enhanceFetchedAsset, enhanceFetchedAssets } from '@/pages/assets/asset-listing/enhanceFetchedAsset';
import ClipGroupModal from '@/pages/assets/clip-group-modal/ClipGroupModal';
import { FuelPortalService } from '@/services';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import type { AssetFilterTypeOptions } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { AssetFilterType } from '@/store/forms/Asset-Editor/AssetFilterTypeOptions';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { assetEditorSelector, UpdateAssetForm } from '@/store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import type { StartJobPayloadExtraParams } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import { batchUpload, startJob } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';
import { dateUtils } from '@/utils/dateUtils';

import FuelSearchInput from '../fuel-search-input';
import FuelTags from '../fuel-tags';
import FuelAssetThumbnail from '../FuelAssetThumbnail/FuelAssetThumbnail';
import FuelAssetTitle from '../FuelAssetTitle/FuelAssetTitle';
import { type DropdownOption, VNDropdown } from '../VNDropdown';
import VNTable from '../VNTable/VNTable';

const columnHelper = createColumnHelper();

const initialPagination: Pagination = {
  sortBy: 'publicationDate',
  descending: true,
  rowsPerPage: 10,
  page: 1,
  totalItems: 0,
  pages: 1,
};

export interface SearchState {
  isLoading: boolean;
  showFileUpload: boolean;
  schemaType: string;
  searchText: string;
  selectedCategories: string[];
  selectedTags: string[];
  extensionOptions: ListItem[];
  selectedExtensions: ListItem[];
  assetTypeOptions: AssetFilterTypeOptions[];
  selectedAssetFilterTypes: AssetFilterTypeOptions[];
  excludeAssetFilterTypes: AssetFilterTypeOptions[];
  pagination: Pagination;
  items: Item[];
  selectedItems: Item[];
  multiple: boolean;
}

const defaultSearchState: SearchState = {
  isLoading: false,
  showFileUpload: false,
  schemaType: '',
  searchText: '',
  selectedCategories: [],
  selectedTags: [],
  extensionOptions: [],
  selectedExtensions: [],
  assetTypeOptions: [],
  selectedAssetFilterTypes: [],
  excludeAssetFilterTypes: [],
  pagination: initialPagination,
  items: [],
  selectedItems: [],
  multiple: false,
};

export interface VNLibraryArgs {
  libraryType: VNLibraryType;
  item: string | Item;
  items: string[] | Item[];
}

export enum VNLibraryType {
  Video = 'Video',
  Image = 'Image',
  Series = 'Series',
  Season = 'Season',
  Extra = 'Extra',
  Trailer = 'Trailer',
}

interface Iprops {
  libraryType?: VNLibraryType;
  onItemSelected?: (event: BaseSyntheticEvent, value: VNLibraryArgs) => void | null;
  onClose?: (event: BaseSyntheticEvent) => void | null;
  multiple?: boolean;
}

const VNLibrary = ({ libraryType = VNLibraryType.Image, onItemSelected = null, onClose = null, multiple = false }: Iprops) => {
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const { alert } = useAppUtils();
  const dispatch = useAppDispatch();

  const stateRef = useRef<SearchState>(defaultSearchState);

  // ------------------------------------------------------------------
  // React Hook Form — VNTable reads data/pagination/selectedRows from here
  // ------------------------------------------------------------------
  const methods = useForm({
    defaultValues: {
      data: [] as Item[],
      pagination: initialPagination as Pagination,
      selectedRows: [] as Item[],
      selectedCategories: [],
      selectedExtensions: [],
      extensionOptions: [],
    },
  });

  const { setValue: setFormValue, getValues: getFormValues, watch: watchForm } = methods;

  // Convenience: keep selectedAssets in sync from form so we can use it
  // in callbacks that are already written against the selectedAssets variable.
  const watchedSelectedRows = watchForm('selectedRows') as Item[];

  useEffect(() => {
    if (multiple === true) {
      stateRef.current.multiple = true;
    }
    initData();
  }, []);

  // Sync form selectedRows → stateRef so websocket handler can read it
  useEffect(() => {
    stateRef.current.selectedItems = watchedSelectedRows ?? [];
  }, [watchedSelectedRows]);

  // ------------------------------------------------------------------
  // Asset Details Modal
  // ------------------------------------------------------------------
  const { assetForm } = useSelector(assetEditorSelector);
  const [openAssetDetailsModal, setOpenAssetDetailsModal] = useState(false);
  const [openClipGroupModal, setOpenClipGroupModal] = useState(false);

  const onTableAction = async (event: BaseSyntheticEvent, type: string, item: Item) => {
    if (type === 'edit' && permissions && permissions.has(Permission.EditAsset)) {
      await openEditAssetPopup(event, item);
    }
  };

  const createAssetForm = async (id: string): Promise<AssetForm> => {
    try {
      let asset = await FuelPortalService.getClipById(id);
      asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
      if (asset) {
        const assetFormValue = await AssetForm.createFromData(asset, permissions, ouData);
        dispatch(UpdateAssetForm(assetFormValue));
        return assetFormValue;
      }
      return null;
    } catch (ex) {
      alert('Error fetching asset.', 'error');
      console.error('Error fetching asset', ex);
      return null;
    }
  };

  const openEditAssetPopup = async (event, item: Item) => {
    if (item.id) {
      const form = await createAssetForm(item.id);
      if (form && form.source === 'ClipGroup') {
        setOpenClipGroupModal(true);
      } else {
        setOpenAssetDetailsModal(true);
      }
    }
  };

  const refreshAssets = async (_items: Item[]) => {
    setProgress(true);
    setTimeout(() => {
      throttledCallback();
      setProgress(false);
    }, 300);
  };

  // ------------------------------------------------------------------
  // Library Modal
  // ------------------------------------------------------------------
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [dropItems, setDropItems] = useState<any>([]);
  const [clearInput, setClearInput] = useState(0);
  const { loading, setProgress } = useProgress();

  const { lastJsonMessage, readyState } = useWebSocketContext();

  const rowRefs = useRef([]);
  const categoryOptions = useMemo<DropdownOption[]>(
    () => (categories ?? []).map((c) => ({ id: c.name, label: c.name })),
    [categories]
  );

  useEffect(() => {
    if (lastJsonMessage !== null) {
      readWebsocketMessage(lastJsonMessage);
    }
  }, [lastJsonMessage]);

  // Helper: read current items from form
  const getCurrentItems = (): Item[] => getFormValues('data') as Item[];

  // Helper: programmatically select a single item in the table
  const selectItem = (_event: SyntheticEvent | null, item: Item) => {
    const newSelection = [item];
    stateRef.current.selectedItems = newSelection;
    setFormValue('selectedRows', newSelection);
  };

  const readWebsocketMessage = async (message) => {
    const currentItems = getCurrentItems();

    if (
      isLibraryOpen &&
      message &&
      message.messageType === MessageType.Notification &&
      message.data &&
      message.data.eventType === PushNotificationEventType.IngestStatusUpdate
    ) {
      const clipId = message.data.id;
      if (clipId) {
        let asset = await FuelPortalService.getClipById(clipId);
        asset = enhanceFetchedAsset(asset, permissions, flags, false, [], [], ouData);
        const index = currentItems.findIndex((clip) => clip.id === clipId);
        if (index > -1) {
          let forceSetSelectedIndex = -1;
          const currentSelected = stateRef.current.selectedItems;
          if (currentSelected.length > 0) {
            forceSetSelectedIndex = currentItems.findIndex((x) => x.id === currentSelected[0].id);
          }

          // carry over special thumbnail
          if (
            (asset.thumbnailSrc == null || asset.thumbnailSrc === '/static/images/thumbnail.png') &&
            currentItems[index].thumbnailSrc != null
          ) {
            if (asset.data.thumbnail == null) {
              asset.data.thumbnail = {};
            }
            asset.data.thumbnail.previewUri = currentItems[index].thumbnailSrc;
            asset.thumbnailSrc = currentItems[index].thumbnailSrc;
          }

          const row = rowRefs.current[clipId];
          const ingestStatusNotCompletedFromExisting = currentItems.find(
            (clip) => clip.id === clipId && clip.data.ingestStatus !== 'Completed'
          );

          let myItems: Item[] = _.cloneDeep(stateRef.current.items);
          myItems = myItems.map((item) => (item.id === clipId ? asset : item));
          stateRef.current.items = myItems;
          setFormValue('data', myItems);

          if (row && asset && asset.data && asset.data.ingestStatus === 'Completed' && ingestStatusNotCompletedFromExisting) {
            row.classList.add('fuel-table-row--highlight');
            setTimeout(() => {
              row.classList.remove('fuel-table-row--highlight');
            }, 2000);
          }

          // carry over selection state
          if (stateRef.current.multiple !== true && forceSetSelectedIndex > -1) {
            setTimeout(() => {
              selectItem(null, myItems[forceSetSelectedIndex]);
            }, 300);
          }
        }
      }
    }
  };

  const onSelect = useCallback(
    (event: BaseSyntheticEvent) =>
      (
        event.currentTarget.closest('tr')?.querySelector('td:first-child input[type="checkbox"]') as HTMLInputElement | null
      )?.click(),
    []
  );

  const Thumbnail = useCallback(
    (cell: CellContext<Item, string>) => (
      <FuelAssetThumbnail item={cell.row.original} permissions={permissions} onClick={onSelect} hideStatus />
    ),
    []
  );

  const getColumns = (columnHelper, libraryType: VNLibraryType) => {
    const videoColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        header: () => 'Title',
        size: 170,
        meta: {
          sortable: true,
          locked: true, //
          headerTitle: 'TITLE',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} onClick={onSelect} />,
      }),
      columnHelper.accessor('extension', {
        align: 'left',
        header: () => 'Type',
        size: 50,
        meta: { sortable: false },
        cell: (cell) => {
          let ext = '';
          if (!clientUtils.isNullOrWhitespace(cell?.row?.original?.data?.originalFileName)) {
            const i = cell.row.original.data.originalFileName.indexOf('.');
            if (i > -1) {
              ext = cell.row.original.data.originalFileName.substring(i);
            }
          }
          return <div title={ext}>{ext}</div>;
        },
      }),
      columnHelper.accessor('publicationDate', {
        header: `Upload Date (${ouData.timezoneAbbreviation})`,
        size: 150,
        meta: { sortable: true },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('durationString', {
        header: 'Duration',
        size: 75,
        meta: { sortable: true },
        cell: (cell) => clientUtils.formatDuration(cell.row.original.duration),
      }),
      columnHelper.accessor('data.accessSettings.effectiveAccessLevel', {
        header: () => `Access`,
        width: 130,
        size: { sortable: false },
        cell: (cell) => cell.row.original.data?.accessSettings?.effectiveAccessLevel || AccessLevelType.Free,
      }),
      columnHelper.accessor('embargoDate', {
        header: () => `Embargo (${ouData.timezoneAbbreviation})`,
        size: 130,
        meta: { sortable: true },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('expireDate', {
        header: `Expiry (${ouData.timezoneAbbreviation})`,
        size: 130,
        meta: { sortable: true },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        size: 130,
        meta: { sortable: true },
        cell: (cell) => (
          <div title={cell.getValue()} className="text-ellipses title">
            {cell.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        size: 130,
        meta: { sortable: true },
        cell: (cell) => (
          <div title={cell.getValue()} className="text-ellipses title">
            {cell.getValue()}
          </div>
        ),
      }),
    ];

    const imageColumns = [
      columnHelper.accessor('thumbnailSrc', {
        header: '',
        cell: Thumbnail,
      }),
      columnHelper.accessor('title', {
        align: 'left',
        header: () => 'Title',
        size: 250,
        meta: {
          sortable: true,
          locked: true, //
          headerTitle: 'TITLE',
          style: {
            textAlign: 'left',
          },
          contentStyle: {
            justifyContent: 'flex-start',
          },
        },
        cell: (cell) => <FuelAssetTitle item={cell.row.original} permissions={permissions} onClick={onSelect} />,
      }),
      columnHelper.accessor('extension', {
        align: 'left',
        header: () => 'Type',
        size: 50,
        meta: { sortable: false },
        cell: (cell) => {
          let ext = '';
          if (!clientUtils.isNullOrWhitespace(cell?.row?.original?.data?.originalFileName)) {
            const i = cell.row.original.data.originalFileName.indexOf('.');
            if (i > -1) {
              ext = cell.row.original.data.originalFileName.substring(i);
            }
          }
          return <div title={ext}>{ext}</div>;
        },
      }),
      columnHelper.accessor('publicationDate', {
        header: `Upload Date (${ouData.timezoneAbbreviation})`,
        size: 150,
        meta: { sortable: true },
        cell: (cell) => dateUtils.format(cell.getValue(), ouData.timezone),
      }),
      columnHelper.accessor('categoryString', {
        header: 'Categories',
        size: 130,
        meta: { sortable: true },
        cell: (cell) => (
          <div className="text-ellipses title" title={cell.getValue()}>
            {cell.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('tagString', {
        header: 'Tags',
        size: 130,
        meta: { sortable: true },
        cell: (cell) => (
          <div className="text-ellipses title" title={cell.getValue()}>
            {cell.getValue()}
          </div>
        ),
      }),
    ];

    if (libraryType === VNLibraryType.Video) {
      return videoColumns;
    }
    return imageColumns;
  };

  const columns = getColumns(columnHelper, libraryType);

  const getPagination = (): Pagination => {
    let paginationNew = getFormValues('pagination') as Pagination;
    if (!paginationNew) {
      paginationNew = _.cloneDeep(initialPagination);
      paginationNew.page = 1;
    }
    return paginationNew;
  };

  const paginationChange = (value: Pagination) => {
    stateRef.current.pagination = value;
    setFormValue('pagination', value);
    throttledCallback();
  };

  const throttledCallback = _.debounce(function () {
    doSearch();
  }, 0);

  const doSearch = async () => {
    setProgress(true);
    const myPagination = getPagination();

    let {
      schemaType,
      searchText,
      selectedCategories,
      selectedTags,
      selectedExtensions,
      selectedAssetFilterTypes,
      excludeAssetFilterTypes,
    } = stateRef.current;

    const extensions = selectedExtensions;
    const categoryNames = selectedCategories;

    const resolveSortBy = () => {
      switch (myPagination.sortBy) {
        case 'categoryString':
          return 'categories';
        case 'tagString':
          return 'tags';
        case 'durationString':
          return 'duration';
        default:
          return myPagination.sortBy;
      }
    };

    selectedAssetFilterTypes = _.uniqBy(selectedAssetFilterTypes, 'id');
    excludeAssetFilterTypes = _.uniqBy(excludeAssetFilterTypes, 'id');

    // Clear selection on new search
    setFormValue('selectedRows', []);
    stateRef.current.selectedItems = [];

    const results = await FuelPortalService.searchAsset(
      searchText,
      ouData.id,
      [],
      myPagination.page - 1,
      myPagination.rowsPerPage,
      categoryNames,
      selectedTags,
      resolveSortBy(),
      myPagination.descending ? 'desc' : 'asc',
      true,
      'clip',
      schemaType,
      false,
      selectedAssetFilterTypes,
      excludeAssetFilterTypes,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      false,
      extensions
    );

    const paginationObj = { ...myPagination };

    if (results && results.data) {
      const assets = enhanceFetchedAssets(results.data, permissions, flags, true, [], [], ouData);
      paginationObj.totalItems = results.pagination.total;
      paginationObj.pages = Math.ceil(paginationObj.totalItems / paginationObj.rowsPerPage);
      stateRef.current.pagination = paginationObj;
      stateRef.current.items = assets;
      setFormValue('pagination', paginationObj);
      setFormValue('data', assets);
    } else {
      paginationObj.totalItems = 0;
      paginationObj.pages = 0;
      stateRef.current.pagination = paginationObj;
      stateRef.current.items = [];
      setFormValue('pagination', paginationObj);
      setFormValue('data', []);
    }

    setProgress(false);
  };

  // ------------------------------------------------------------------
  // Region: Search Control
  // ------------------------------------------------------------------

  const onSearchTextChanged = (value) => {
    stateRef.current.searchText = value;
  };

  const onSearchTextClick = (value) => {
    if (value != null) {
      stateRef.current.searchText = value;
    }
    onSearchClick();
  };

  const resetPagination = () => {
    const paginationObject = getFormValues('pagination') as Pagination;
    if (paginationObject != null) {
      paginationObject.page = 1;
      paginationObject.totalItems = 0;
      paginationObject.pages = 1;
      setFormValue('pagination', paginationObject);
    }
  };

  const getFreshState = (): SearchState => {
    const defaults: SearchState = _.cloneDeep(defaultSearchState);
    const newState: SearchState = _.cloneDeep(stateRef.current);

    newState.schemaType = getSchemaType(libraryType);
    newState.assetTypeOptions = defaults.assetTypeOptions;
    newState.searchText = defaults.searchText;
    newState.selectedCategories = defaults.selectedCategories;
    newState.selectedTags = defaults.selectedTags;
    newState.selectedExtensions = defaults.selectedExtensions;
    methods.setValue('selectedCategories', defaults.selectedCategories);
    methods.setValue('selectedExtensions', defaults.selectedExtensions);
    return newState;
  };

  const resetSearchState = () => {
    setClearInput(clearInput + 1);
    const newState = getFreshState();
    stateRef.current = newState;
  };

  const getSchemaType = (libraryType: VNLibraryType) => {
    if (libraryType === VNLibraryType.Image) {
      return SchemaType.Image;
    }
    return SchemaType.Video;
  };

  const onResetClick = () => {
    resetSearchState();
    resetPagination();
    throttledCallback();
  };

  const onSearchClick = () => {
    resetPagination();
    throttledCallback();
  };

  const onTagsChanged = (value) => {
    stateRef.current.selectedTags = value;
  };

  const onCategoriesChanged = (value) => {
    // console.log('value', { value });
    stateRef.current.selectedCategories = value;
    methods.setValue('selectedCategories', value);
  };

  // ------------------------------------------------------------------
  // Region: Extension Dropdown
  // ------------------------------------------------------------------

  const onExtensionsChanged = (val) => {
    stateRef.current.selectedExtensions = val;
    methods.setValue('selectedExtensions', val);
  };

  // ------------------------------------------------------------------
  // Region: Asset Types Dropdown
  // ------------------------------------------------------------------

  const onAssetFilterTypesChanged = (val) => {
    stateRef.current.selectedAssetFilterTypes = val;
  };

  // ------------------------------------------------------------------
  // Region: Base Component Button Actions
  // ------------------------------------------------------------------

  const initData = () => {
    if (stateRef.current.isLoading === false || (stateRef.current.isLoading === true && stateRef.current.schemaType === '')) {
      try {
        stateRef.current.isLoading = true;
        resetPagination();
        const newState = getFreshState();
        newState.isLoading = true;

        if (libraryType === VNLibraryType.Trailer) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [{ id: AssetFilterType.Trailer, name: AssetFilterType.Trailer }];
          newState.excludeAssetFilterTypes = [{ id: AssetFilterType.Broadcast, name: AssetFilterType.Broadcast }];
        } else if (libraryType === VNLibraryType.Extra) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [{ id: AssetFilterType.Extra, name: AssetFilterType.Extra }];
          newState.excludeAssetFilterTypes = [{ id: AssetFilterType.Broadcast, name: AssetFilterType.Broadcast }];
        } else if (libraryType === VNLibraryType.Series) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [{ id: AssetFilterType.Series, name: AssetFilterType.Series }];
          newState.excludeAssetFilterTypes = [{ id: AssetFilterType.Broadcast, name: AssetFilterType.Broadcast }];
        } else if (libraryType === VNLibraryType.Season) {
          newState.extensionOptions = [];
          newState.selectedAssetFilterTypes = [{ id: AssetFilterType.Season, name: AssetFilterType.Season }];
          newState.excludeAssetFilterTypes = [{ id: AssetFilterType.Broadcast, name: AssetFilterType.Broadcast }];
        } else if (libraryType === VNLibraryType.Video) {
          newState.showFileUpload = true;
          newState.extensionOptions = [
            { id: 'mov', name: '.mov' },
            { id: 'mpeg4', name: '.mpeg4' },
            { id: 'mxf', name: '.mxf' },
            { id: 'wmv', name: '.wmv' },
            { id: 'mp4', name: '.mp4' },
            { id: 'mpeg', name: '.mpeg' },
            { id: 'mpg', name: '.mpg' },
            { id: 'mkv', name: '.mkv' },
          ];
          newState.selectedAssetFilterTypes = [
            { id: AssetFilterType.NestedChannel, name: 'Nested/Collections' },
            { id: AssetFilterType.ClipGroup, name: 'Clip Groups' },
            { id: AssetFilterType.LiveProgramAssets, name: 'Live Program Assets' },
            { id: AssetFilterType.VODFileAssets, name: 'VOD File Assets' },
            { id: AssetFilterType.ExternalClip, name: 'ExternalClip' },
          ];
          newState.excludeAssetFilterTypes = [
            { id: AssetFilterType.MediaLibrary, name: AssetFilterType.MediaLibrary },
            { id: AssetFilterType.Broadcast, name: AssetFilterType.Broadcast },
          ];
        } else if (libraryType === VNLibraryType.Image) {
          newState.showFileUpload = true;
          newState.extensionOptions = [
            { id: 'jpeg', name: '.jpeg' },
            { id: 'jpg', name: '.jpg' },
            { id: 'png', name: '.png' },
            { id: 'gif', name: '.gif' },
            { id: 'svg', name: '.svg' },
          ];
          newState.selectedAssetFilterTypes = [{ id: AssetFilterType.Image, name: 'Images' }];
          newState.excludeAssetFilterTypes = [];
        }
        newState.extensionOptions = newState.extensionOptions.map((e) => ({ id: e.id, name: e.name, label: e.name }));
        methods.setValue('extensionOptions', newState.extensionOptions);
        stateRef.current = newState;
        throttledCallback();
      } finally {
        setTimeout(() => {
          stateRef.current.isLoading = false;
        }, 0);
      }
    }
  };

  const handleLibraryClose = (event: BaseSyntheticEvent) => {
    onClose?.(event);
  };

  const createNewCollection = (event: BaseSyntheticEvent) => {
    onItemSelected?.(event, { libraryType, item: 'new', items: [] });
  };

  // ------------------------------------------------------------------
  // Table Item Selection — driven by VNTable via form
  // ------------------------------------------------------------------

  const handleItemSelected = (event: BaseSyntheticEvent) => {
    const selected = getFormValues('selectedRows') as Item[];
    if (selected && selected.length > 0) {
      onItemSelected?.(event, { libraryType, item: selected[0], items: selected });
      onClose?.(event);
    }
  };

  // ------------------------------------------------------------------
  // Drag and Drop Files
  // ------------------------------------------------------------------
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [isDragOverActive, setDragOverActive] = useState(false);

  const onBrowseButtonClick = (event: BaseSyntheticEvent) => {
    event.stopPropagation();
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverActive(true);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverActive(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverActive(false);
  };

  const onDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverActive(false);
  };

  const onFileDrop = (event: React.DragEvent<HTMLDivElement>, _info) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverActive(false);
    const { files } = event.dataTransfer;
    processFileChange(files);
  };

  const getAssetTitle = (filename: string) => {
    return filename.replace('-', ' ').replace('_', ' ');
  };

  const getAssetForm = (form: AssetForm, file: File) => {
    const newAssetForm: AssetForm = _.cloneDeep(form);
    const fileName = getAssetTitle(file.name);
    newAssetForm.title = fileName;
    newAssetForm.originalFileName = file.name;
    newAssetForm.ingestFile = file;
    return newAssetForm;
  };

  const getImgBlobsAsync = async (fileList: FileList) => {
    const promises = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      const ext = clientUtils.getExtension(file.name);

      if (libraryType === VNLibraryType.Image) {
        promises.push(cliptoolUtils.getFileImageBlob(file));
      } else if (ext === '.mxf' || ext === '.mpg' || ext === '.mov' || ext === '.wmv' || ext === '.mkv') {
        promises.push(new Promise<string>((resolve) => resolve(null)));
      } else {
        promises.push(cliptoolUtils.getVideoThumbnail(file));
      }
    }
    const results = await Promise.all(promises);
    return results;
  };

  const processFileChange = async (fileList: FileList) => {
    if (fileList == null || fileList.length <= 0) {
      alert(`No file selected.  Please try again.`, 'warning');
      return;
    }
    if (stateRef.current.multiple !== true && fileList.length > 1) {
      alert(`Multiple files not allowed.  Please select only one file.`, 'warning');
      return;
    }

    const schemaType = stateRef.current.schemaType === 'Video' ? SchemaType.Video : SchemaType.Image;

    const files: File[] = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const warningMessage = clientUtils.validateFileExtension(schemaType, fileList[i].name);
      if (warningMessage != null) {
        alert(warningMessage, 'warning');
        return;
      }

      const file: File = fileList[0];
      const maxFileSize = MAX_FILE_SIZE_20GB;
      const fileSize = file.size;
      if (fileSize >= maxFileSize.fileSize) {
        alert(`Maximum allowed file size is ${maxFileSize.name}`, 'error');
        return;
      }

      files.push(fileList[i]);
    }

    try {
      const imgBlobs: string[] = await getImgBlobsAsync(fileList);

      const myAssetForm = await AssetForm.create(permissions, ouData);
      myAssetForm.originalFileName = '';
      myAssetForm.schemaType = schemaType;
      myAssetForm.ingestFile = null;
      myAssetForm.ingestFiles = files;
      myAssetForm.title = '';
      myAssetForm.source = AssetSource.Ingest;

      const extraParams: StartJobPayloadExtraParams = {
        returnItemCallback: (item: Item, index) => {
          if (item == null) return;

          const enhancedItem = enhanceFetchedAsset(item, permissions, flags, true, [], [], ouData);

          if (imgBlobs.length > index && imgBlobs[index] != null) {
            const imgBlob = imgBlobs[index];
            if (enhancedItem.data.thumbnail == null) {
              enhancedItem.data.thumbnail = {};
            }
            if (imgBlob == null) {
              enhancedItem.thumbnailSrc = '/static/images/thumbnail.png';
            } else {
              enhancedItem.data.thumbnail.tempThumbnailBlob = imgBlob;
              enhancedItem.thumbnailSrc = imgBlob;
            }
          }

          const myItems: Item[] = _.cloneDeep(stateRef.current.items);
          const mySelectedItems: Item[] = _.cloneDeep(stateRef.current.selectedItems);
          const myPagination = _.cloneDeep(stateRef.current.pagination);

          myItems.unshift(enhancedItem);
          mySelectedItems.unshift(enhancedItem);
          stateRef.current.selectedItems = mySelectedItems;
          stateRef.current.items = myItems;

          myPagination.rowsPerPage += 1;
          myPagination.totalItems += 1;
          stateRef.current.pagination = myPagination;

          // Push updates into form so VNTable re-renders
          setFormValue('data', myItems);
          setFormValue('pagination', myPagination);

          if (!stateRef.current.multiple) {
            setTimeout(() => {
              setFormValue('selectedRows', [myItems[0]]);
              stateRef.current.selectedItems = [myItems[0]];
            }, 300);
          } else {
            setTimeout(() => {
              setFormValue('selectedRows', mySelectedItems);
            }, index * 100);
          }
        },
      };

      if (files.length === 1) {
        const file: File = files[0];
        const frozenForm = getAssetForm(myAssetForm, file);
        const startJobResult = await dispatch(startJob({ form: frozenForm, jobType: '', extraParams }));

        if (startJobResult?.payload?.error?.message != null) {
          console.error(startJobResult?.payload?.error?.message);
        }

        if (startJobResult?.payload?.payload !== true) {
          alert('There was an error uploading your asset.  Please try again and contact support if the issue persists.', 'error');
        }
      } else {
        const frozenForm = _.cloneDeep(myAssetForm);
        const newAssetForms: AssetForm[] = [];
        for (let i = 0; i < files.length; i += 1) {
          newAssetForms.push(getAssetForm(frozenForm, files[i]));
        }

        const response = await dispatch(batchUpload({ forms: newAssetForms, jobType: 'BatchUpload', extraParams }));
        if (response && response.payload) {
          const assetsSavedSuccessfully = response.payload.filter((r) => r.status === 'fulfilled');
          const errorCount = response.payload.filter((r) => r.status !== 'fulfilled');
          if (errorCount > 0) {
            alert(
              `Not all assets created - ${assetsSavedSuccessfully.length} created successfully, ${errorCount} failed.`,
              'error'
            );
          } else {
            alert(`All ${assetsSavedSuccessfully.length} Assets created successfully.`);
          }
        }
      }
    } catch (ex) {
      console.error({ ex });
    }

    if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  // ------------------------------------------------------------------
  // Derived: is the Select button enabled?
  // ------------------------------------------------------------------
  const isSelectDisabled = !watchedSelectedRows || watchedSelectedRows.length === 0;
  return (
    <div className="vn-library">
      <VNBaseModal
        cssClassName="vn-library-modal"
        open
        width="xl"
        title={`Select ${libraryType} Asset`.toUpperCase()}
        onClose={handleLibraryClose}
      >
        <VnModalBody>
          <Grid container spacing={2}>
            {/* ── Search sidebar ───────────────────────────────────── */}
            <Grid className="search-area" item xs={2}>
              <Grid item xs={12}>
                <FuelSearchInput
                  defaultValue={stateRef.current.searchText}
                  onSearch={onSearchTextClick}
                  onChange={onSearchTextChanged}
                  clearInput={clearInput}
                />
              </Grid>
              <Grid item xs={12}>
                <FuelTags
                  id="fuel-ingest-tag-id"
                  tags={stateRef.current.selectedTags}
                  inputTagWidth={365}
                  onChange={onTagsChanged}
                />
              </Grid>
              <Grid item xs={12}>
                {/* <FuelAutoComplete
                  id="category-select"
                  items={categories}
                  itemText="name"
                  itemKey="id"
                  labelText="Categories"
                  placeHolderText="Select Categories"
                  controlWidth={365}
                  multiSelect
                  labelPaddingTop="-5px"
                  defaultValue={stateRef.current.selectedCategories}
                  onChange={onCategoriesChanged}
                /> */}
                <VNDropdown
                  label="Categories"
                  placeholder="Select Categories"
                  searchable="autocomplete"
                  multiple
                  onChange={onCategoriesChanged}
                  options={categoryOptions}
                  value={methods.watch('selectedCategories')}
                />
              </Grid>
              {methods.getValues('extensionOptions')?.length > 0 && (
                <Grid item xs={12}>
                  {/* <FuelAutoComplete
                    id="extensions-select"
                    items={stateRef.current.extensionOptions}
                    itemText="name"
                    itemKey="id"
                    labelText="Extension"
                    placeHolderText="Select"
                    multiSelect
                    defaultValue={stateRef.current.selectedExtensions}
                    onChange={onExtensionsChanged}
                  /> */}
                  <VNDropdown
                    label="Extension"
                    placeholder="Select Extension"
                    searchable="autocomplete"
                    multiple
                    onChange={onExtensionsChanged}
                    options={methods.watch('extensionOptions')}
                    value={methods.watch('selectedExtensions')}
                  />
                </Grid>
              )}
              <Grid item xs={12} style={{ textAlign: 'right' }}>
                <Button style={{ margin: '0px 8px' }} variant="contained" onClick={onResetClick}>
                  Reset
                </Button>
                <Button color="primary" variant="contained" onClick={onSearchClick}>
                  Search
                </Button>
              </Grid>

              {stateRef.current.showFileUpload && (
                <Grid item xs={12}>
                  <div
                    id="ingest-widget"
                    className={classSet({ 'drag-over-active': isDragOverActive })}
                    onDragOver={onDragOver}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDragEnd={onDragEnd}
                    onDrop={onFileDrop}
                    test-id="ingest-widget"
                    onClick={onBrowseButtonClick}
                  >
                    <form encType="multipart/form-data">
                      <div className="ingest-drop">
                        <i className="icon material-icons">backup</i>
                        <p>Click to upload or drag and drop</p>
                        <p>(Max File Size 10GB)</p>
                      </div>
                      <div className="file-section">
                        <input
                          className="file-input"
                          type="file"
                          id="file"
                          ref={fileInput}
                          onChange={(e) => processFileChange(e.target.files)}
                          multiple={stateRef.current.multiple}
                        />
                        <Button
                          variant="contained"
                          style={{ fontSize: '13px', marginTop: '5px' }}
                          size="small"
                          className="browse-button"
                          color="primary"
                          onClick={onBrowseButtonClick}
                        >
                          BROWSE
                        </Button>
                      </div>
                    </form>
                  </div>
                </Grid>
              )}
            </Grid>

            {/* ── Table area ───────────────────────────────────────── */}
            <Grid item xs={10}>
              <div className="library-table-container">
                {/*
                  FormProvider wraps VNTable so it can access watch/setValue
                  via useFormContext internally.
                  data/pagination/selectedRows are all managed through the form.
                */}
                <FormProvider {...methods}>
                  <VNTable
                    dataKey="data"
                    columns={columns}
                    showRowSelection
                    paginationChange={paginationChange}
                    loading={loading}
                    rowRefs={rowRefs}
                    selectionMode={stateRef.current.multiple ? 'multi' : 'single'}
                    tableId="vn-asset-library-table"
                  />
                </FormProvider>
              </div>
            </Grid>
          </Grid>
        </VnModalBody>

        <VnModalFooter
          right={
            <>
              {(libraryType === VNLibraryType.Extra ||
                libraryType === VNLibraryType.Season ||
                libraryType === VNLibraryType.Trailer) && (
                  <Button color="primary" variant="contained" onClick={createNewCollection}>
                    {`Create New ${libraryType}`}
                  </Button>
                )}
              <Button variant="contained" onClick={handleLibraryClose}>
                Cancel
              </Button>
              <Button disabled={isSelectDisabled} color="primary" variant="contained" onClick={handleItemSelected}>
                Select
              </Button>
            </>
          }
        />
      </VNBaseModal>

      {openAssetDetailsModal && (
        <AssetDetailsModal
          open={openAssetDetailsModal}
          onClose={() => setOpenAssetDetailsModal(false)}
          processAssetsReturnedFromSaving={refreshAssets}
        />
      )}
      {openClipGroupModal && (
        <ClipGroupModal
          open={openClipGroupModal}
          onClose={() => setOpenClipGroupModal(false)}
          processAssetsReturnedFromSaving={refreshAssets}
          assetForm={assetForm}
        />
      )}
    </div>
  );
};

export default VNLibrary;
